import React, { useEffect, useContext, useState } from 'react'
import {
  MAP_API_KEY,
  basedMapStyle,
  defaultMapCenter,
  defaultMapZoom,
  siteZoomLevels,
  PAGE_TEMPLATE
} from '../../services/api/constants'
import {
  GlobalMapProps,
  CountryCode,
  CountryZoomObject,
  LAYOUT_TEMPLATE
} from '../../services/api/types'
import ReactMapboxGl, { ZoomControl, RotationControl } from 'react-mapbox-gl'
import { GymsMapLayers } from './GymsMapLayers'
import {
  LocationMapContainer,
  MapContainerStyled,
  ToggleMapButton
} from './LocationMap.styled'
import markerImg from '../../static/imgs/location-marker.png'
import markerNewImg from '../../static/imgs/location-marker-new.png'
import useWindowSize from '../../hooks/useWindowSize'
import { SiteContext } from '../../services/context/SiteContext'
import { getSiteCode } from '../../services/api/helpers'
import { ButtonLink } from '../base'

export interface LocationMapProps extends GlobalMapProps {
  refElm?: any
  mapStyle?: string
  locationQuery?: string
  mapZoom?: [number]
  centerCoord?: [number, number]
  isIndex?: boolean
  onLoad?: Function
  states?: string[]
  googleMapDirection?: any
  template?: LAYOUT_TEMPLATE
}

const Map = ReactMapboxGl({
  accessToken: MAP_API_KEY,
  scrollZoom: false,
  dragPan: false
})

export const LocationMap = (props: LocationMapProps) => {
  const {
    mapStyle,
    mapZoom,
    centerCoord,
    mapData,
    locationQuery,
    pinIcon,
    displaySub,
    refElm,
    isIndex,
    onLoad,
    states,
    googleMapDirection,
    template
  } = props
  const [zoomLevel, setZoom] = useState<[number]>(mapZoom || defaultMapZoom)

  const { siteInfor } = useContext(SiteContext)
  const siteCode: CountryCode = getSiteCode(siteInfor?.root_url)
  const siteZoom: CountryZoomObject = siteZoomLevels[siteCode]

  // () => fly to map point
  const zoomTo = async (event: any) => {
    const { lat, lng } = event.lngLat
    const { map } = refElm.current.state
    const { z } = event.target.style // current map zoom level
    map.flyTo({
      center: [lng, lat],
      zoom: [z + 2]
    })
  }
  const windowSize = useWindowSize()

  useEffect(() => {
    setTimeout(function() {
      if (!refElm || (refElm && !refElm.current)) return
      const { map } = refElm.current?.state
      map.scrollZoom.disable()
      if (windowSize?.width) {
        map.on('mousedown', function() {
          map.scrollZoom.enable()
          map.dragPan.enable()
        })
        // only for gym index pages
        if (isIndex && !locationQuery) {
          if (windowSize.width > 767) {
            setZoom(siteZoom.desktop)
          } else {
            setZoom(siteZoom.mobile)
          }
        }
      }
    }, 1000)
  }, [windowSize.width])

  const markerIcon = new Image()
  markerIcon.src =
    template === PAGE_TEMPLATE.YOU_FITNESS
      ? markerNewImg
      : pinIcon
      ? pinIcon
      : markerImg

  return (
    <LocationMapContainer>
      <Map
        zoom={zoomLevel || defaultMapZoom}
        center={centerCoord || defaultMapCenter}
        style={mapStyle || basedMapStyle}
        ref={refElm}
        containerStyle={MapContainerStyled}
        movingMethod='flyTo'
        onStyleLoad={async e => {
          e.resize()
          onLoad && (await onLoad())
        }}
      >
        <ZoomControl position='top-left' />
        <RotationControl position='top-left' />
        <GymsMapLayers
          pinIcon={pinIcon}
          mapData={mapData}
          zoomTo={zoomTo}
          displaySub={displaySub}
          locationMarker={markerIcon}
          states={states}
          template={template}
        />
      </Map>
      {googleMapDirection && (
        <ToggleMapButton>
          <ButtonLink
            href={googleMapDirection.url}
            color='blue'
            target={googleMapDirection.target}
            className='py-2'
          >
            {googleMapDirection.label}
          </ButtonLink>
        </ToggleMapButton>
      )}
    </LocationMapContainer>
  )
}

export default LocationMap
